import styled from 'styled-components';
import { Close as CloseIcon } from '@mui/icons-material';
import { IconButton, InputLabel } from '@mui/material';

import { StyledSelect, StyledTextField } from 'components/Input/styles';

import { ACTION_COLUMN_WIDTH, ROW_NUMBER_COLUMN_WIDTH } from './constants';

export const StyledRiskAssessmentContainer = styled.div<{ $selected?: boolean }>`
  /* Cap height to one page in Pages mode to break an unbounded page-creation loop (ENG-11115).
     Same protection the riskMatrix node has via StyledRiskMatrixFullDisplay. */
  max-height: var(--page-max-height);
  /* 1px transparent border reserves space to avoid layout shift when selection kicks in. */
  border: 1px solid
    ${({ theme, $selected }) => ($selected ? theme.palette.primary.main : 'transparent')};
  border-radius: 8px;
  margin: 16px 0;
  position: relative;
  overflow: auto;
  transition: border-color 120ms ease, box-shadow 120ms ease;
  /* Uses NodeViewProps.selected, not .ProseMirror-selectednode — the class placement on the wrapper DOM isn't reliable. */
  box-shadow: ${({ theme, $selected }) =>
    $selected ? `0 0 0 1px ${theme.palette.primary.main}` : 'none'};

  /* :has(.Mui-focused) covers MUI inputs whose focus moved into a portal (date picker,
     autocomplete) — :focus-within alone misses those. */
  &:focus-within,
  &:has(.Mui-focused) {
    border-color: transparent;
    box-shadow: none;
  }
`;

export const StyledRiskAssessmentBody = styled.div`
  padding: 16px 4px;
`;

// Holds the scroller + our custom scrollbar so the bar can sit directly under the
// table at the visible (non-scrolled) width.
export const StyledTableScrollContainer = styled.div`
  position: relative;
`;

export const StyledRiskAssessmentTableWrapper = styled.div`
  overflow-x: auto;
  /* Hide the native scrollbar on every platform; we render our own always-visible
     custom bar below. macOS overlay scrollbars auto-hide (no reliable "scroll me" cue)
     and a styled WebKit bar still renders as an overlay there, so the native bar can't
     be trusted as the affordance. */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

export const StyledCustomScrollbar = styled.div<{ $visible: boolean }>`
  display: ${({ $visible }) => ($visible ? 'block' : 'none')};
  position: relative;
  height: 8px;
  margin-top: 8px;
  border-radius: 999px;
  background: ${({ theme }) => theme.palette.grey[100]};
`;

export const StyledCustomScrollbarThumb = styled.div`
  position: absolute;
  top: 0;
  height: 100%;
  min-width: 24px;
  border-radius: 999px;
  background: ${({ theme }) => theme.palette.grey[400]};
  cursor: grab;
  transition: background-color 120ms ease;

  &:hover {
    background: ${({ theme }) => theme.palette.grey[500]};
  }
  &:active {
    background: ${({ theme }) => theme.palette.grey[600]};
    cursor: grabbing;
  }
`;

export const StyledRiskAssessmentTable = styled.table`
  && {
    border-collapse: collapse;
    /* Both need !important — '.tiptap table { width: auto !important }' (table-node.scss) would
       win otherwise; '&&' (0,2,0) beats its (0,1,1). table-layout:fixed makes <col> widths
       authoritative so cell content can't resize a column (no jump when a rating is picked). */
    width: max-content !important;
    table-layout: fixed !important;
    /* Pages extension rewrites all tables to CSS grid so they can split across page breaks —
       that collapses our header cells and ignores the colgroup. Force native table layout back;
       !important beats the extension's constructable stylesheet. */
    display: table !important;
  }
  && thead {
    display: table-header-group !important;
  }
  && tbody {
    display: table-row-group !important;
  }
  && tr {
    display: table-row !important;
    grid-template-columns: none !important;
    /* Pages extension leaves width/max-width/overflow on rows; reset so resize handles aren't clipped. */
    width: auto !important;
    max-width: none !important;
    overflow: visible !important;
  }
  && th,
  && td {
    display: table-cell !important;
  }
`;

export const StyledHeaderCell = styled.th`
  position: relative;
  padding: var(--tt-table-cell-padding, 0.5rem);
  font-weight: 700;
  text-align: left;
  vertical-align: middle;
  color: ${({ theme }) => theme.palette.text.primary};
  white-space: normal;
  word-wrap: break-word;
  border: 1px solid var(--tt-table-border-color, ${({ theme }) => theme.palette.grey[300]});
  /* Doubled selector beats '.ProseMirror th' (hover row bg) and '.tiptap th' (forced cell padding). */
  && {
    background-color: var(--tt-gray-light-a-100, ${({ theme }) => theme.palette.grey[50]});
    padding-block: 6px;
  }
`;

export const StyledBodyCell = styled.td`
  padding: 0;
  border: 1px solid var(--tt-table-border-color, ${({ theme }) => theme.palette.grey[300]});
  /* Top-align — narrow columns produce tall wrapped rows where middle-align floats short cells. */
  vertical-align: top;
  background: ${({ theme }) => theme.palette.background.paper};
  transition: background-color 120ms ease;
  overflow: hidden;

  /* ::after overlay instead of box-shadow — TableKit's [data-border-top] beats inset shadows in
     Pages mode (ring appeared on 3 sides only). z-index:1 on the cell lifts it so the overlay
     isn't clipped by neighbours. */
  &&:focus-within {
    position: relative;
    z-index: 1;
  }
  &&:focus-within::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 2;
    border: 2px solid ${({ theme }) => theme.palette.primary.main};
    border-radius: 2px;
    pointer-events: none;
  }
`;

export const StyledCellInputWrapper = styled.div`
  width: 100%;

  && .MuiOutlinedInput-root {
    border-radius: 0;
    background: transparent;
  }

  && .MuiOutlinedInput-notchedOutline,
  &&:hover .MuiOutlinedInput-notchedOutline,
  && .Mui-focused .MuiOutlinedInput-notchedOutline,
  && .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline,
  && .MuiPickersOutlinedInput-notchedOutline,
  &&:hover .MuiPickersOutlinedInput-notchedOutline,
  && .Mui-focused .MuiPickersOutlinedInput-notchedOutline {
    border: 0 !important;
  }

  && .MuiInputBase-input,
  && .MuiInputBase-input::placeholder,
  && .MuiSelect-select,
  && .MuiPickersInputBase-sectionContent,
  && .MuiPickersSectionList-sectionContent {
    font-size: inherit;
    font-family: inherit;
  }

  /* Let every descendant shrink below its content width so narrow columns don't
     force the table wider. Scoped to this wrapper, so the wildcard is safe. */
  && * {
    min-width: 0;
  }

  && .MuiFormControl-root,
  && .MuiPickersTextField-root,
  && .MuiPickersInputBase-root,
  && .MuiPickersOutlinedInput-root {
    width: 100%;
    color: ${({ theme }) => theme.palette.text.primary};
  }

  && .MuiPickersInputBase-root,
  && .MuiPickersOutlinedInput-root {
    padding: 0;
  }

  && .MuiPickersInputBase-sectionsContainer,
  && .MuiPickersSectionList-root {
    padding: 8px 0 8px 8px;
  }

  && .MuiInputAdornment-root {
    margin: 0 8px 0 0;
  }

  && .MuiInputAdornment-root .MuiIconButton-root {
    padding: 4px;
  }

  && .MuiOutlinedInput-input,
  && .MuiInputBase-input {
    padding: 4px;
  }

  && .MuiInputBase-multiline {
    padding: 0;
  }

  && .MuiInputBase-inputMultiline {
    padding: 4px;
  }

  && .MuiSelect-select {
    height: auto;
    min-height: 0;
    /* Keep the wider right inset for the dropdown arrow; left matches the 4px text inset. */
    padding: 4px 32px 4px 4px;
  }

  && .MuiOutlinedInput-root,
  && .MuiInputBase-root {
    height: auto;
    min-height: 0;
  }
`;

// 4px inset matches the input padding so values line up with the editable render;
// min-height keeps an empty cell the same one-line height a single-row input would have.
export const StyledReadOnlyCellText = styled.div`
  padding: 4px;
  font-size: inherit;
  font-family: inherit;
  color: ${({ theme }) => theme.palette.text.primary};
  white-space: pre-wrap;
  word-break: break-word;
  min-height: calc(1em + 8px);
`;

export const StyledColumnResizer = styled.div`
  position: absolute;
  top: 0;
  right: -3px;
  width: 6px;
  height: 100%;
  cursor: col-resize;
  user-select: none;
  z-index: 1;

  &:hover,
  &:active {
    background: ${({ theme }) => theme.palette.primary.main};
    opacity: 0.4;
  }
`;

export const StyledRowActionsCell = styled.td`
  border-bottom: 1px solid ${({ theme }) => theme.palette.grey[200]};
  width: ${ACTION_COLUMN_WIDTH}px;
  text-align: center;
  vertical-align: top;
  /* Doubled selector (0,2,0) beats '.tiptap td' 5px padding (0,1,1) — without it the action column can't hold its slim width. */
  && {
    padding: 4px 1px;
  }
`;

// MUI IconButton size="small" has a 30px floor that forces the action column too wide; padding:2px shrinks it to fit ACTION_COLUMN_WIDTH.
export const StyledRowDeleteButton = styled(IconButton)`
  && {
    padding: 2px;
    color: ${({ theme }) => theme.palette.text.secondary};

    & svg {
      font-size: 18px;
    }
  }
`;

export const StyledEmptyState = styled.td`
  text-align: center;
  color: ${({ theme }) => theme.palette.text.secondary};
  font-size: 13px;
  font-style: italic;
  /* Doubled selector (0,2,0) beats '.tiptap td' 5px padding (0,1,1). */
  && {
    padding: 8px 16px;
  }
`;

export const StyledFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  /* Doubled selector needed — '.tiptap *' (0,1,1) zeros all margins on node descendants. */
  && {
    margin-top: 20px;
  }
`;

export const StyledFooterActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

export const StyledLegendStrip = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
  padding: 10px 0 0;
  margin-top: 8px;
  border-top: 1px dashed ${({ theme }) => theme.palette.grey[200]};
  font-size: 12px;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

export const StyledLegendItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

export const StyledLegendSwatch = styled.span<{ $bg: string }>`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ $bg }) => $bg};
`;

export const StyledRowNumberHeaderCell = styled.th`
  padding: var(--tt-table-cell-padding, 0.5rem) 2px;
  font-weight: 700;
  text-align: center;
  vertical-align: middle;
  color: ${({ theme }) => theme.palette.text.primary};
  border: 1px solid var(--tt-table-border-color, ${({ theme }) => theme.palette.grey[300]});
  width: ${ROW_NUMBER_COLUMN_WIDTH}px;
  min-width: ${ROW_NUMBER_COLUMN_WIDTH}px;
  && {
    background-color: var(--tt-gray-light-a-100, ${({ theme }) => theme.palette.grey[50]});
    padding-block: 6px;
  }
`;

export const StyledRowNumberCell = styled.td`
  /* Doubled selector forces the padding over the editor's '.tiptap td' override. 7px top
     (not 4px) so the row number's first line lines up with the text cells: the multiline
     text input sits ~3px below the cell top before its own 4px padding, so its text
     starts ~7px down. Matching that keeps the number, text and trash icon on one line. */
  && {
    padding: 7px 2px 4px;
  }
  text-align: center;
  /* nowrap so a two-digit row index stays on one line: the editor's global
     overflow-wrap/word-break would otherwise split "10" across two rows in this
     narrow column. */
  white-space: nowrap;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.palette.text.secondary};
  border: 1px solid var(--tt-table-border-color, ${({ theme }) => theme.palette.grey[300]});
  background: ${({ theme }) => theme.palette.grey[50]};
  vertical-align: top;
  width: ${ROW_NUMBER_COLUMN_WIDTH}px;
  min-width: ${ROW_NUMBER_COLUMN_WIDTH}px;
`;

export const StyledRiskRatingCell = styled.button<{ $disabled?: boolean }>`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px;
  background: transparent;
  border: none;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  font-size: inherit;
  font-family: inherit;
  color: ${({ theme }) => theme.palette.text.secondary};
  text-align: left;

  &:hover {
    background: ${({ $disabled, theme }) =>
      $disabled ? 'transparent' : theme.palette.action.hover};
  }

  &:focus-visible {
    outline: none;
  }
`;

export const StyledRiskRatingChipLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.02em;
  flex-shrink: 0;
`;

// Two-line stack so the rating column stays narrow enough for an A4 portrait page.
export const StyledRiskRatingContent = styled.span`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1px;
  flex: 1;
  min-width: 0;
`;

export const StyledRiskRatingLine = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
`;

// Revealed on hover/focus only so it doesn't compete with the chips for column width.
export const StyledRiskRatingChevron = styled.span`
  position: absolute;
  top: 2px;
  right: 0;
  display: inline-flex;
  align-items: center;
  color: ${({ theme }) => theme.palette.text.secondary};
  opacity: 0;
  transition: opacity 120ms ease;
  pointer-events: none;

  ${StyledRiskRatingCell}:hover &,
  ${StyledRiskRatingCell}:focus-within & {
    opacity: 1;
  }
`;

export const StyledRiskRatingChip = styled.span<{ $bg?: string; $color?: string; $bold?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 15px;
  padding: 1px 3px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: ${({ $bold }) => ($bold ? 700 : 600)};
  background-color: ${({ $bg, theme }) => $bg ?? theme.palette.grey[200]};
  color: ${({ $color, theme }) => $color ?? theme.palette.text.primary};
`;

export const StyledRiskRatingChipSeparator = styled.span`
  color: ${({ theme }) => theme.palette.text.secondary};
  font-size: 12px;
  font-weight: 500;
`;

export const StyledRiskRatingPopoverContent = styled.div`
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const StyledRiskRatingMatrixWithAxis = styled.div`
  display: flex;
  align-items: stretch;
  gap: 4px;
`;

export const StyledRiskRatingSeverityAxisLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  /* Reads bottom-to-top. Under vertical-rl each glyph turns 90deg CW (so a "->" points
     down) and rotate(180deg) flips the whole label upright, leaving the arrow pointing
     up — matching the horizontal "Likelihood ->". */
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  font-size: 12px;
  font-style: italic;
  color: ${({ theme }) => theme.palette.text.secondary};
  white-space: nowrap;
`;

export const StyledRiskRatingMatrixGrid = styled.div<{ $cols: number }>`
  display: grid;
  grid-template-columns: auto auto repeat(${({ $cols }) => $cols}, 56px);
  gap: 2px;
`;

export const StyledRiskRatingAxisLabel = styled.div<{ $wrap?: boolean; $bottomAxis?: boolean; $cols?: number }>`
  font-size: 12px;
  font-weight: ${({ $wrap, $bottomAxis }) => ($wrap || $bottomAxis ? 400 : 600)};
  font-style: ${({ $bottomAxis }) => ($bottomAxis ? 'italic' : 'normal')};
  color: ${({ theme }) => theme.palette.text.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  white-space: ${({ $wrap }) => ($wrap ? 'normal' : 'nowrap')};
  max-width: ${({ $wrap }) => ($wrap ? '100px' : 'none')};
  overflow-wrap: anywhere;
  ${({ $bottomAxis, $cols }) =>
    $bottomAxis && $cols ? `grid-column: 3 / span ${$cols};` : ''}
`;

export const StyledRiskRatingHeaderCell = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.text.secondary};
  text-align: center;
  padding: 4px 2px;
  line-height: 1.2;
`;

export const StyledRiskRatingHeaderLabel = styled.div`
  font-weight: 400;
  font-size: 11px;
  overflow-wrap: anywhere;
`;

export const StyledRiskRatingMatrixCell = styled.button<{
  $bg?: string;
  $color?: string;
  $selected?: boolean;
}>`
  height: 36px;
  border: ${({ $selected, theme }) =>
    $selected ? `2px solid ${theme.palette.primary.main}` : '1px solid transparent'};
  background-color: ${({ $bg }) => $bg ?? 'transparent'};
  color: ${({ $color, theme }) => $color ?? theme.palette.text.primary};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border-radius: 2px;

  &:hover {
    filter: brightness(0.92);
  }
`;

export const StyledRiskRatingPopoverFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 4px;
`;

export const StyledSectionTitle = styled.p`
  font-size: 15px;
  margin: 0 0 4px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.grey[900]};
`;

export const StyledSectionDescription = styled.p<{ $tight?: boolean }>`
  font-size: 13px;
  margin: ${({ $tight }) => ($tight ? '0 0 12px' : '0 0 16px')};
  color: ${({ theme }) => theme.palette.grey[600]};
`;

export const StyledLabelIndex = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.grey[600]};
  width: 18px;
  text-align: right;
`;

export const StyledConfigDialogContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
  margin-top: 8px;
`;

export const StyledMatrixSizeRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-end;
`;

export const StyledMatrixSizeField = styled(StyledTextField)`
  width: 96px;
`;

export const StyledMatrixSizeCloseIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
`;

export const StyledMatrixSizeCloseIcon = styled(CloseIcon)`
  && {
    font-size: 20px;
    color: ${({ theme }) => theme.palette.grey[600]};
  }
`;

export const StyledMatrixSizeLabel = styled(InputLabel)`
  && {
    font-size: 12px;
    margin-bottom: 4px;
  }
`;

export const StyledLabelsRow = styled.div`
  display: flex;
  gap: 24px;
`;

export const StyledLabelsColumn = styled.div`
  flex: 1;
`;

export const StyledLabelsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const StyledLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const StyledColumnsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
`;

export const StyledColumnRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
`;

export const StyledColumnTypeSelect = styled(StyledSelect)`
  &.MuiInputBase-root {
    height: 40px;
  }
  & .MuiSelect-select {
    max-height: 40px;
    padding: 8px 32px 8px 14px;
    min-height: 0;
  }
`;

export const StyledColumnActions = styled.div`
  display: flex;
  gap: 2px;
`;

export const StyledAddColumnRow = styled.div`
  margin-top: 12px;
`;
